import React, { useCallback, useState } from "react";
import { GPUInstanceType } from "types/InstanceTypes";
import { LaunchableNotFound } from "./LaunchableNotFound";
import {
  getLaunchableNgcApiKeySpec,
  ProcessedLaunchableData,
} from "components/Launchables/utils";
import { LaunchableAccessError } from "./LaunchableAccessError";
import {
  resolveLaunchableSkin,
  isPhysicalAISkin,
  LAUNCHABLE_SKIN,
} from "components/Launchables/DeployRedesign/shared/content/skins/registry";
import { STORAGE_KEYS } from "constants/localStorage";
import { safeLocalStorageSetItem } from "utils/localStorage";
import { EditComputeModal } from "components/Launchables/DeployRedesign/EditComputeModal";
import { LaunchableFromList } from "server";

import { LaunchableDeploymentProvider } from "components/Launchables/DeployRedesign/shared/deployment/LaunchableDeploymentContext";
import { LaunchableDeployAction } from "components/Launchables/DeployRedesign/shared/deployment/LaunchableDeployAction";
import { LaunchableConfiguration } from "components/Launchables/DeployRedesign/shared/configuration/LaunchableConfiguration";
import { LaunchableDeploymentConfirmation } from "components/Launchables/DeployRedesign/shared/deployment/LaunchableDeploymentConfirmation";
import { Spinner } from "@kui-react/spinner";
import type { NgcApiKeyDeployResult } from "components/Launchables/DeployRedesign/shared/configuration/ngc-api-key/NgcApiKeyOption";
import type { LaunchableParameterBinding, NGCAPIKeySpec } from "server";
import { DeployPageLayout } from "./DeployPageLayout";
import { LaunchableAbout } from "components/Launchables/DeployRedesign/shared/content/LaunchableAbout";
import { LaunchableHeading } from "components/Launchables/DeployRedesign/shared/content/LaunchableHeading";
import { LaunchableContentTabs } from "components/Launchables/DeployRedesign/shared/content/tabs/LaunchableContentTabs";
import { LaunchableLoadError } from "./LaunchableLoadError";
import { useLaunchableDeployment } from "../hooks/useLaunchableDeployment";

interface DeployPageContentProps {
  loading: boolean;
  launchableId?: string | null;
  launchableNotFound?: boolean;
  launchableAccessError?: boolean;
  launchableLoadError?: boolean;
  retryingLaunchable?: boolean;
  onRetryLaunchable?: () => void;
  launchableData?: ProcessedLaunchableData | null;
  rawLaunchableData?: LaunchableFromList | null;
  intendedInstanceType: string | null;
  intendedCloudCredId: string | null;
  intendedDiskStorage: string | null;
  defaultInstanceType: GPUInstanceType | null;
  onSetNewCurrentInstanceType?: (
    instanceTypeObject: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  instanceTypeObject: GPUInstanceType | null;
  instanceLoading: boolean;
  instanceStorage: string | null;
  nameErrorMessage: string;
  allInstancesAvailable: GPUInstanceType[];
  onDeploy: (
    ngcApiKeySpec?: NGCAPIKeySpec | null,
    parameterBindings?: LaunchableParameterBinding[],
    location?: string | null,
  ) => Promise<NgcApiKeyDeployResult | void> | NgcApiKeyDeployResult | void;
  lifecycleScript?: string | null;
  isPublic?: boolean;
  instanceName?: string;
  onInstanceNameChange?: (name: string) => void;
  initialLocation?: string | null;
}

interface DeploymentEditComputeModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

const DeploymentEditComputeModal: React.FC<DeploymentEditComputeModalProps> = ({
  show,
  setShow,
}) => {
  const {
    intendedInstanceType,
    instanceType,
    imageId,
    intendedCloudCredentialId,
    onInstanceChange,
  } = useLaunchableDeployment();

  return (
    <EditComputeModal
      intendedInstanceType={intendedInstanceType}
      currentInstanceType={instanceType}
      show={show}
      setShow={setShow}
      imageId={imageId}
      lockedCloudProvider={imageId ? intendedCloudCredentialId : null}
      onSuccess={(selectedInstanceType, storage) => {
        const selectedStorage = selectedInstanceType.elastic_root_volume
          ? storage || "120"
          : selectedInstanceType.supported_storage?.[0]?.size || "120";
        onInstanceChange?.(selectedInstanceType, selectedStorage);
      }}
    />
  );
};

export const DeployPageContent: React.FC<DeployPageContentProps> = ({
  loading,
  launchableId,
  launchableNotFound,
  launchableAccessError,
  launchableLoadError,
  retryingLaunchable,
  onRetryLaunchable,
  launchableData,
  rawLaunchableData,
  intendedInstanceType,
  intendedCloudCredId,
  intendedDiskStorage,
  defaultInstanceType,
  onSetNewCurrentInstanceType,
  instanceLoading,
  instanceTypeObject,
  instanceStorage,
  nameErrorMessage,
  allInstancesAvailable,
  onDeploy,
  lifecycleScript,
  isPublic,
  instanceName,
  onInstanceNameChange,
  initialLocation,
}) => {
  const [showEditComputeModal, setShowEditComputeModal] = useState(false);

  const nemoClawLaunchableId = import.meta.env.VITE_NEMOCLAW_LAUNCHABLE_ID;
  const resolvedLaunchableId = launchableId ?? launchableData?.launchableId;

  const activeSkin = resolveLaunchableSkin(resolvedLaunchableId);
  const hasPhysicalAISkin = isPhysicalAISkin(activeSkin);

  const handleDeploy = useCallback(
    (
      ngcApiKeySpec?: NGCAPIKeySpec | null,
      parameterBindings?: LaunchableParameterBinding[],
      selectedLocation?: string | null,
    ) => {
      if (resolvedLaunchableId === nemoClawLaunchableId) {
        safeLocalStorageSetItem(STORAGE_KEYS.nemoClawBannerDismissed, "true");
      }
      return onDeploy(ngcApiKeySpec, parameterBindings, selectedLocation);
    },
    [resolvedLaunchableId, nemoClawLaunchableId, onDeploy],
  );

  const lowerContent = (
    <LaunchableContentTabs
      launchableData={launchableData}
      lifecycleScript={lifecycleScript}
      activeSkin={activeSkin}
    />
  );

  const deployLabel =
    activeSkin === LAUNCHABLE_SKIN.NEMOCLAW
      ? "Deploy NemoClaw"
      : hasPhysicalAISkin
        ? "Create Agent"
        : undefined;

  const priceDisclaimer =
    activeSkin === LAUNCHABLE_SKIN.NEMOCLAW
      ? "This price covers the Brev instance only. AI model endpoint usage on build.nvidia.com is billed separately by NVIDIA and is not included."
      : undefined;

  const launchableHeading = (
    <LaunchableHeading
      activeSkin={activeSkin}
      name={launchableData?.name}
      description={launchableData?.description}
    />
  );

  const renderMainContent = () => {
    if (!loading && launchableNotFound) {
      return <LaunchableNotFound />;
    }

    if (!loading && launchableAccessError) {
      return <LaunchableAccessError isPublic={Boolean(isPublic)} />;
    }

    if (!loading && launchableLoadError) {
      return (
        <LaunchableLoadError
          retrying={retryingLaunchable}
          onRetry={onRetryLaunchable}
        />
      );
    }

    return (
      <LaunchableDeploymentProvider
        instanceType={instanceTypeObject}
        defaultInstanceType={defaultInstanceType}
        intendedInstanceType={intendedInstanceType}
        intendedDiskStorage={intendedDiskStorage}
        instanceStorage={instanceStorage}
        instanceLoading={instanceLoading}
        intendedCloudCredentialId={intendedCloudCredId}
        intendedFirewallRules={launchableData?.firewallRules || null}
        imageId={launchableData?.imageId || null}
        loading={loading}
        isPublic={isPublic}
        ngcApiKeySpec={getLaunchableNgcApiKeySpec(rawLaunchableData)}
        parameters={launchableData?.parameters || []}
        nameErrorMessage={nameErrorMessage}
        allInstances={allInstancesAvailable}
        onDeploy={handleDeploy}
        onInstanceChange={onSetNewCurrentInstanceType}
        deployLabel={deployLabel}
        priceDisclaimer={priceDisclaimer}
        instanceName={instanceName}
        onInstanceNameChange={onInstanceNameChange}
        initialLocation={
          initialLocation !== undefined
            ? initialLocation
            : launchableData
              ? launchableData.location
              : undefined
        }
      >
        <DeploymentEditComputeModal
          show={showEditComputeModal}
          setShow={setShowEditComputeModal}
        />
        <DeployPageLayout
          deploymentAction={
            <LaunchableDeployAction
              heading={launchableHeading}
              launchableName={launchableData?.name ?? undefined}
            />
          }
          configuration={
            <LaunchableConfiguration
              about={
                <LaunchableAbout
                  authorUserId={rawLaunchableData?.createdByUserId}
                  updatedAt={rawLaunchableData?.updatedAt}
                />
              }
              onShowEditComputeModal={() => setShowEditComputeModal(true)}
            />
          }
          lowerContent={lowerContent}
        />
        <LaunchableDeploymentConfirmation
          onShowEditComputeModal={() => setShowEditComputeModal(true)}
        />
      </LaunchableDeploymentProvider>
    );
  };

  return (
    <>
      <div className="mx-auto min-h-screen max-w-7xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        {loading ? (
          <div className="h-screen flex justify-center items-center -mt-10">
            <Spinner />
          </div>
        ) : (
          renderMainContent()
        )}
      </div>
    </>
  );
};
